import type { AppMessages } from '../../types';

/** English app translations — imported by en.ts and used as reference for other locales */
export const app: AppMessages = {
  common: {
    loading: 'Loading...',
    saving: 'Saving...',
    save: 'Save',
    back: 'Back',
    error: 'Error',
    notSet: 'Not set',
    user: 'User',
    system: 'System',
    copied: 'Copied!',
    copy: 'Copy',
    cancel: 'Cancel',
    creating: 'Creating...',
    processing: 'Processing...',
    you: 'You',
    stars: 'Stars',
    seeAll: 'See All',
    filter: 'Filter',
    amount: 'Amount',
    date: 'Date',
    details: 'Details',
    mustBeLoggedIn: 'You must be logged in',
    daysShort: 'd',
    hoursShort: 'h',
    minutesShort: 'min',
    justNow: 'Just now',
    noTitle: 'No title',
    payment: 'payment',
    payments: 'payments',
  },
  layout: {
    dashboard: 'Dashboard',
    invoices: 'Invoices',
    clients: 'Clients',
    ledger: 'Ledger',
    withdraws: 'Withdraws',
    redirects: 'Redirects',
    settings: 'Settings',
    admin: 'Admin',
    profile: 'Profile',
    logout: 'Logout',
    tonConfigured: 'TON Configured',
    configureTon: 'Configure TON',
    tonNotConfigured: 'TON address not configured',
    active: 'Active',
    walletConnect: 'Connect wallet',
    walletTitle: 'Connect your TON wallet',
    walletSubtitle: 'Enter the TON address that will receive your withdrawals.',
    walletConnectedTitle: 'Wallet connected',
    walletConnectedSubtitle: 'Your withdrawals will be sent to this address.',
    walletCurrentAddress: 'Connected address',
    walletAddressLabel: 'TON address',
    walletHint: 'User-friendly (EQ… / UQ…) or raw (0:…) format accepted.',
    walletInvalidAddress: 'Invalid TON address',
    walletUpdate: 'Update address',
    walletChangeAddress: 'Change address',
    walletDisconnect: 'Disconnect',
    walletDisconnectConfirmTitle: 'Disconnect wallet?',
    walletDisconnectConfirmBody: 'You will not be able to request withdrawals until you connect a new address.',
    walletSaved: 'Wallet connected successfully',
    walletDisconnected: 'Wallet disconnected',
    newPaymentLink: 'New payment link',
    openBot: 'Telegram bot',
    helpCenter: 'Help center',
  },
  login: {
    seoTitle: 'Login | Telegram Stars Payment Platform',
    seoDescription: 'Login to your Telegram Stars Payment Platform account',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    email: 'Email',
    password: 'Password',
    displayNameOptional: 'Display name (optional)',
    displayNamePlaceholder: 'Your name',
    emailPlaceholder: 'your@email.com',
    signInButton: 'Sign in',
    signUpButton: 'Sign up',
    signInWithGoogle: 'Sign in with Google',
    or: 'OR',
    welcome: 'Welcome',
    subtitle: 'Sign in or create an account to get started',
    minPasswordHint: 'Minimum 6 characters',
    tipTelegram: '💡 Tip: You can link your Telegram account in settings after signing up',
    continueWithTelegram: 'Continue with Telegram',
    telegramAuthError: 'Telegram sign-in failed. Please try again.',
    featureInstant: 'Instant account creation',
    featureManage: 'Manage your Telegram Stars payments',
    featureWithdraw: 'Withdraw your funds to TON',
    emailRequired: 'Please enter your email',
    emailInvalid: 'Invalid email format',
    passwordRequired: 'Please enter your password',
    passwordRequiredSignup: 'Please enter a password',
    passwordMinLength: 'Password must contain at least 6 characters',
    genericError: 'An error occurred',
    recaptchaFailed: 'reCAPTCHA verification failed. Please try again.',
    invalidCredentials: 'Incorrect email or password. If you don\'t have an account yet, create one in the "Sign Up" tab.',
    wrongPassword: 'Incorrect password. Please check your password.',
    tooManyAttempts: 'Too many attempts. Please try again later.',
    accountDisabled: 'This account has been disabled. Contact support.',
    authNotEnabled: 'Email/password authentication is not enabled. Check Firebase configuration.',
    emailInUse: 'This email is already in use. Sign in with this email.',
    googleAuthError: 'An error occurred during Google authentication',
    popupClosed: 'Popup was closed. Please try again.',
    popupBlocked: 'Popup was blocked. Please allow popups and try again.',
    popupCancelled: 'Only one popup request is allowed at a time.',
  },
  dashboard: {
    loading: 'Loading dashboard...',
    overallSummary: 'Overall Summary',
    lastMonth: 'Last month',
    lastWeek: 'Last week',
    lastYear: 'Last year',
    balance: 'Balance',
    available: 'Available',
    totalEarned: 'Total Earned',
    fromLastMonth: 'from last month',
    recentPayments: 'Recent Payments',
    last7days: 'Last 7 days',
    last30days: 'Last 30 days',
    last14days: 'Last 14 days',
    applyPeriod: 'Apply',
    vsPreviousPeriod: 'vs previous period',
    quickActions: 'Quick Actions',
    createLink: 'Create Link',
    createLinkDesc: 'Generate a new payment link',
    withdraw: 'Withdraw',
    withdrawDesc: 'Withdraw your Stars to TON',
    invoices: 'Invoices',
    invoicesDesc: 'View payment history',
    ledger: 'Ledger',
    ledgerDesc: 'View all transactions',
    recentActivity: 'Recent Activity',
    noRecentActivity: 'No recent activity',
    mostRecent: 'Most Recent',
    noRecentInvoices: 'No recent invoices',
    allInvoices: 'All invoices',
    paymentReceived: 'Payment received: {title}',
    withdrawalRequested: 'Withdrawal requested: {amount} {suffix}',
    transaction: 'Transaction: {amount} {suffix}',
    invoiceLabel: 'Invoice #{id}',
    failedLoadBalance: 'Failed to load balance',
    chartAmount: 'Amount',
  },
  invoices: {
    loading: 'Loading...',
    allInvoices: 'All Invoices',
    paid: 'Paid',
    sent: 'Sent',
    created: 'Created',
    expired: 'Expired',
    filter: 'Filter',
    createInvoice: '+ Create Invoice',
    noInvoices: 'No invoices found',
    relatedTo: 'Related to:',
    paymentMode: '{mode} Payment',
    copyLink: 'Copy Link',
    copyLinkTitle: 'Copy invoice link',
    linkNotAvailable: 'Invoice link not available',
    linkCopied: 'Invoice link copied to clipboard!',
    copyFailed: 'Failed to copy link',
    failedLoad: 'Failed to load invoices',
    statusPaid: 'paid',
    statusSent: 'sent',
    statusCreated: 'created',
    statusExpired: 'expired',
    typeSingleUse: 'single-use',
    typeRecurring: 'recurring',
  },
  createInvoice: {
    back: 'Back',
    title: 'Create a payment link',
    subtitle: 'Generate a new payment link in seconds',
    successTitle: 'Payment link created!',
    successSubtitle: 'Your payment link is ready to be shared',
    paymentLink: 'Payment link:',
    integrationTitle: 'Integrate this link on your website',
    integrationDesc: 'Our Telegram bot guides you step by step (API, HTML button, redirects).',
    openTutorial: 'Open tutorial',
    testLink: 'Test link',
    viewInvoices: 'View my invoices',
    createNew: 'Create a new link',
    shareTelegram: 'Share on Telegram',
    paymentMode: 'Payment mode',
    modeStars: 'Stars',
    modeEur: 'EUR (€)',
    modeUsd: 'USD ($)',
    amountInteger: 'Amount (integer)',
    amountDecimal: 'Amount (decimal)',
    amountRequired: 'Please enter an amount',
    amountIntegerError: 'Amount must be an integer greater than 0',
    amountNoDecimals: 'Stars amount must be an integer (no decimals)',
    amountMustBeInteger: 'Stars amount must be an integer',
    amountGreaterThanZero: 'Amount must be greater than 0',
    titleOptional: 'Title (optional)',
    titlePlaceholder: 'Payment title (e.g., Payment for services)',
    descriptionOptional: 'Description (optional)',
    descriptionPlaceholder: 'Detailed payment description...',
    singleUse: 'Single-use link',
    singleUseTooltip: 'If enabled, this link can only be used once. Once paid, it will no longer be valid.',
    createLink: 'Create link',
    cancel: 'Cancel',
    invalidAmount: 'Invalid amount',
    starsMustBeInteger: 'Amount must be an integer for Stars',
    createError: 'Error creating invoice',
  },
  ledger: {
    title: 'Ledger',
    typeColumn: 'Type',
    description: 'Complete history of all your transactions',
    loading: 'Loading...',
    noEntries: 'No transactions at the moment.',
    typePayment: 'Payment',
    typeFee: 'Fee',
    typeNetPayment: 'Net Payment',
    typeWithdrawal: 'Withdrawal',
    typeAdjustment: 'Adjustment',
    amountStars: 'Amount (Stars)',
    invoiceDetail: 'Invoice: {title} ({amount} Stars)',
    paidBy: 'Paid by: {payer}',
    payerId: '(ID: {id})',
    withdrawDetail: 'Withdraw: {amount} Stars (Status: {status})',
    failedLoad: 'Failed to load ledger',
  },
  withdraws: {
    title: 'My Withdrawal Requests',
    newRequest: '+ New Request',
    totalLedger: 'Total ledger',
    available: 'Available',
    pendingWithdrawals: 'Pending withdrawals',
    hold21days: '21-day hold',
    payoutNote: 'Payouts are processed manually by an admin. A 3% fee applies at withdrawal.',
    forecastTitle: 'Withdrawal Forecast',
    loadingForecast: 'Loading forecast...',
    noForecast: 'No upcoming withdrawal dates',
    paymentsCount: '({count} payments)',
    noRequests: 'No withdrawal requests at the moment.',
    createFirst: 'Create your first request',
    amount: 'Amount:',
    destination: 'Destination:',
    processedOn: 'Processed on:',
    externalRef: 'External reference:',
    errorLabel: 'Error:',
    rejectionReason: 'Rejection reason:',
    failedLoad: 'Failed to load withdraws',
    loading: 'Loading...',
  },
  createWithdraw: {
    title: 'Request a Withdrawal',
    balanceSource: 'Balance (source: backend ledger)',
    available: 'Available: {amount} Stars',
    pending: 'Pending withdrawals: {amount} Stars',
    waiting: 'Waiting 21-day hold: {amount} Stars',
    minAndFee: 'Minimum withdrawal: {min} Stars · Platform fee: 3% on payout (manual processing)',
    amountLabel: 'Amount (Stars)',
    minPlaceholder: 'Minimum: {min}',
    feeHint: 'Gross: {gross} Stars · Fee (3%): {fee} Stars · You receive: {net} Stars',
    useRegistered: 'Use my registered TON address',
    registeredAddress: 'Registered address:',
    tonDestination: 'TON destination address',
    tonPlaceholder: 'EQAbc123...',
    tonFormatHint: 'Format: EQ... or 0:... (base64 or hex)',
    createRequest: 'Create request',
    cancel: 'Cancel',
    creating: 'Creating...',
    insufficientBalance: '⚠️ Insufficient balance for withdrawal (minimum: {min} Stars)',
    amountMustBeInteger: 'Amount must be a positive integer',
    createError: 'Error creating withdrawal request',
  },
  redirects: {
    title: 'Redirect links',
    description: 'Create short links that redirect to a URL of your choice. Sharing the link /r/your-slug will send visitors to the configured URL.',
    newRedirect: 'New redirect',
    clicks: 'clicks',
    unique: 'unique',
    links: 'links',
    modalTitle: 'New redirect',
    slugLabel: 'Slug (link identifier)',
    slugRequired: 'Required field',
    slugPattern: 'Letters, numbers, hyphens and underscores only',
    slugMax: 'Max 64 characters',
    slugReserved: 'This slug is reserved. Choose another.',
    slugPlaceholder: 'e.g.: my-site',
    urlLabel: 'Destination URL',
    urlInvalid: 'Invalid URL',
    urlHttpOnly: 'Only http and https URLs are accepted.',
    urlTooLong: 'URL too long (max 2048 characters).',
    urlPlaceholder: 'https://example.com/page',
    createButton: 'Create redirect',
    loading: 'Loading…',
    empty: 'No redirects. Click "New redirect" to create one.',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    deleteTitle: 'Delete this redirect?',
    yes: 'Yes',
    no: 'No',
    delete: 'Delete',
    totalClicks: 'Total clicks',
    uniqueClicks: 'Unique clicks',
    avgClicks: 'Avg. clicks/visitor',
    loadError: 'Error loading redirects',
    createSuccess: 'Redirect link created',
    createError: 'Error creating redirect',
    deleteSuccess: 'Redirect deleted',
    deleteError: 'Error deleting redirect',
  },
  adminWithdraws: {
    title: 'Administration - Withdrawal Requests',
    filterByStatus: 'Filter by status:',
    all: 'All',
    noRequests: 'No requests for this status.',
    agent: 'Agent:',
    amount: 'Amount:',
    destination: 'Destination:',
    processedOn: 'Processed on:',
    externalRef: 'External ref:',
    errorLabel: 'Error:',
    reason: 'Reason:',
    approve: '✓ Approve',
    reject: '✗ Reject',
    markPaid: '✓ Mark Paid',
    markFailed: '✗ Mark Failed',
    approveConfirm: 'Approve this withdrawal request?',
    rejectPrompt: 'Rejection reason:',
    markPaidPrompt: 'External payout reference (required — TON tx hash, bank ref, etc.):',
    markPaidRequired: 'externalRef is required to confirm a manual payout.',
    markFailedPrompt: 'Error message:',
    approveError: 'Error approving withdrawal',
    rejectError: 'Error rejecting withdrawal',
    updateError: 'Error updating',
    failedLoad: 'Failed to load withdraws',
    notAuthenticated: 'Not authenticated',
    operationFailed: 'Operation failed',
    loading: 'Loading...',
  },
  redirectResolve: {
    missingSlug: 'Missing slug',
    invalidLink: 'Invalid link',
    notFound: 'Link not found',
    notFoundTitle: 'Link not found',
    redirecting: 'Redirecting...',
    redirectTitle: 'Redirect',
  },
  errorBoundary: {
    title: 'An error occurred',
    subtitle: 'Sorry, something went wrong. Please try again.',
    goHome: 'Back to home',
    reload: 'Reload page',
    devDetails: 'Error details (dev only):',
  },
  table: {
    search: 'Search',
    sort: 'Sort',
    filter: 'Filter',
    open: 'Open',
    count: 'Count',
    empty: 'No results',
    clearSort: 'Remove sort',
    ascending: 'Ascending',
    descending: 'Descending',
    viewTable: 'Table',
    properties: 'Properties',
  },
  invoicesTable: {
    colTitle: 'Name',
    colDescription: 'Description',
    colStatus: 'Status',
    colType: 'Type',
    colAmount: 'Price',
    colPayments: 'Payments',
    colCreated: 'Created',
    colLink: 'Payment link',
    colMode: 'Mode',
    detailTitle: 'Payment link',
    openLink: 'Open in Telegram',
  },
  clients: {
    title: 'Clients',
    subtitle: 'Everyone who paid one of your links, with amounts and status.',
    viewClients: 'Clients',
    viewPurchases: 'Purchases',
    colClient: 'Client',
    colUsername: 'Username',
    colTelegramId: 'Telegram ID',
    colPurchases: 'Purchases',
    colTotal: 'Total spent',
    colLastPurchase: 'Last purchase',
    colLastProduct: 'Last product',
    colStatus: 'Status',
    colProduct: 'Product',
    colPrice: 'Price',
    colDate: 'Date',
    statusNew: 'New',
    statusReturning: 'Returning',
    statusVip: 'VIP',
    statusPaid: 'Paid',
    anonymous: 'Anonymous buyer',
    noClients: 'No client yet — share a payment link to get your first sale.',
    noPurchases: 'No purchase yet.',
    failedLoad: 'Failed to load clients',
    purchasesOf: 'Purchases',
    kpiClients: 'Clients',
    kpiPurchases: 'Purchases',
    kpiRevenue: 'Revenue',
    kpiAverage: 'Average basket',
  },
  projects: {
    all: 'All projects',
    newProject: 'New project',
    createTitle: 'New project',
    editTitle: 'Edit project',
    name: 'Name',
    namePlaceholder: 'e.g. January coaching',
    invoices: 'Invoices',
    invoicesHint: 'Choose the invoices that belong to this project.',
    searchInvoices: 'Search invoices',
    noInvoices: 'No invoice yet.',
    noMatch: 'No invoice matches.',
    create: 'Create project',
    save: 'Save',
    delete: 'Delete',
    confirmDelete: 'Confirm delete',
    saving: 'Saving...',
    cancel: 'Cancel',
    failed: 'Could not save the project',
    nameRequired: 'Give the project a name',
    emptyList: 'No project yet',
    loadingInvoices: 'Loading invoices...',
    selectedCount: '{count} selected',
  },
  adminUsers: {
    menuLabel: 'Users',
    menuWithdraws: 'Withdraws (admin)',
    viewAll: 'All',
    viewActive: 'Active (7d)',
    viewInactive: 'Inactive (14d+)',
    viewNoTelegram: 'No Telegram',
    viewNoLink: 'No link created',
    viewEmailed: 'Emailed',
    colUser: 'User',
    colStatus: 'Status',
    colTelegram: 'Telegram',
    colLinks: 'Links',
    colRevenue: 'Revenue',
    colLastSignIn: 'Last sign-in',
    colLastActivity: 'Last action',
    colSignedUp: 'Signed up',
    colEmails: 'Follow-ups',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    statusNew: 'New',
    statusDisabled: 'Disabled',
    linked: 'Linked',
    notLinked: 'Not linked',
    never: 'Never',
    noEmail: 'No email',
    kpiUsers: 'Users',
    kpiActive7d: 'Active 7d',
    kpiInactive: 'Inactive',
    kpiRevenue: 'Total revenue',
    loginAs: 'Sign in as',
    loginAsConfirmTitle: 'Open a session as this user?',
    loginAsConfirm: 'You will see the app exactly as they do. Every action will be logged as performed by an admin. A banner lets you return to your own account.',
    loginAsStarted: 'Signed in as {name}',
    loginAsError: 'Could not open the session',
    sendEmail: 'Send follow-up email',
    emailTitle: 'Follow up with {name}',
    emailTemplate: 'Template',
    emailSubject: 'Subject',
    emailMessage: 'Message',
    emailSend: 'Send',
    emailSent: 'Email sent to {email}',
    emailError: 'Sending failed',
    emailNotConfigured: 'Email sending is not configured on the server (SMTP_PASS or RESEND_API_KEY missing).',
    emailNoAddress: 'This user has no email address.',
    emailTitleBulk: 'Follow up with {count} users',
    emailSentBulk: '{sent} emails sent',
    emailSentPartial: '{sent} sent, {failed} failed',
    emailSelectSend: 'Send email',
    emailSelected: '{count} selected',
    emailTagsHint: 'Each recipient gets their own version. Tags: {name}, {email}, {appUrl}, {bot}.',
    emailTagsLabel: 'Insert',
    templateCustom: 'Custom message',
    templateInactive: 'Inactivity follow-up',
    templateNoLink: 'No payment link created',
    templateNoTelegram: 'Telegram not linked',
    templateUnpaid: 'Links awaiting payment',
    tabActivity: 'Activity',
    tabInvoices: 'Links',
    tabPayments: 'Payments',
    tabEmails: 'Follow-ups',
    activityEmpty: 'No action recorded yet.',
    invoicesEmpty: 'No payment link.',
    paymentsEmpty: 'No payment received.',
    emailsEmpty: 'No follow-up sent.',
    byAdmin: 'by admin',
    profile: 'Profile',
    userId: 'User ID',
    tonAddress: 'TON address',
    bannerTitle: 'Admin session — signed in as {name}',
    bannerHint: 'Your actions are logged as performed by an admin.',
    bannerExit: 'Back to my account',
    bannerRestored: 'Back on your admin account',
    bannerExpired: 'Admin session expired: please sign in again.',
    refresh: 'Refresh',
    sendTelegram: 'Follow up on Telegram',
    telegramTitle: 'Follow up with {name} on Telegram',
    telegramSent: 'Telegram message sent',
    telegramError: 'Telegram sending failed',
    telegramNotLinked: 'This user has not linked a Telegram account.',
    telegramHint: 'Sent by the bot with the open-app buttons. The user must have tapped /start on the bot.',
    channelEmail: 'Email',
    channelTelegram: 'Telegram',
  },
};

export const settingsApp = {
  eyebrow: 'SETTINGS',
  profile: 'Profile',
  email: 'Email',
  telegramId: 'Telegram ID',
  notLinked: 'Not linked',
  linking: 'Linking...',
  linkTelegram: 'Link my Telegram account',
  openFromTelegram: '⚠️ Open this page from Telegram to link your account',
  displayName: 'Display name',
  displayNameHint: 'Display name is set when creating the account',
  withdrawal: 'Withdrawal',
  tonAddress: 'TON destination address',
  tonAddressPlaceholder: 'EQAbc123... or 0:...',
  tonAddressHint: 'This address will be used by default for your withdrawals',
  tonUpdated: 'TON address updated successfully',
  about: 'About',
  commission: 'Platform commission:',
  commissionValue: '3% on each payment',
  minWithdrawal: 'Minimum withdrawal:',
  minWithdrawalValue: '100 Stars',
  starsCurrency: 'Stars currency:',
  starsCurrencyValue: 'XTR',
  telegramWebAppNotAvailable: 'Telegram WebApp not available. Open this page from Telegram.',
  telegramUserDataNotAvailable: 'Telegram user data not available.',
  telegramLinked: 'Telegram account linked successfully!',
  linkTelegramError: 'Error linking Telegram account',
  updateError: 'Error updating',
  mustBeLoggedIn: 'You must be logged in',
};
